// src/pages/Admin/CoursAgenda/components/CoursAgendaDetailsDrawer.jsx

import React from "react";

import {
  FaCalendarAlt,
  FaClock,
  FaCopy,
  FaEdit,
  FaMapMarkerAlt,
  FaTrash,
  FaUser,
  FaUserFriends,
} from "react-icons/fa";

import CoursAgendaDrawerShell from "./CoursAgendaDrawerShell";

import {
  formatCourseDate,
  getPersonName,
} from "../utils/coursAgendaUtils";

const PeopleSection = ({
  title,
  people = [],
  icon: Icon,
  emptyText,
}) => (
  <div
    className="
      rounded-2xl
      border border-slate-200
      bg-slate-50
      p-4
      dark:border-slate-800
      dark:bg-slate-900
    "
  >
    <div className="mb-3 flex items-center gap-2">
      <Icon className="text-red-500" />

      <h4 className="font-semibold text-slate-950 dark:text-white">
        {title}
      </h4>

      <span className="ml-auto rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-500 shadow-sm dark:bg-slate-800 dark:text-slate-300">
        {people.length}
      </span>
    </div>

    {people.length > 0 ? (
      <div className="space-y-2">
        {people.map((person) => (
          <div
            key={person.id}
            className="
              flex items-center gap-3
              rounded-xl
              border border-slate-200
              bg-white
              px-3 py-2.5
              dark:border-slate-700
              dark:bg-slate-950
            "
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-200">
              {(person.prenom?.[0] ||
                person.nom?.[0] ||
                "?")
                .toString()
                .toUpperCase()}
            </div>

            <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
              {getPersonName(person)}
            </span>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-sm italic text-slate-400">
        {emptyText}
      </p>
    )}
  </div>
);

const CoursAgendaDetailsDrawer = ({
  open,
  cours,
  onClose,

  onEdit,
  onDuplicate,
  onDelete,

  deleting = false,
}) => {
  if (!cours) return null;

  const start =
    cours.heure_debut?.slice(0, 5) ||
    "--:--";

  const end =
    cours.heure_fin?.slice(0, 5) ||
    "--:--";

  const footer = (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      <button
        type="button"
        onClick={() => onEdit?.(cours)}
        className="
          flex items-center justify-center gap-2
          rounded-xl
          bg-slate-900
          px-4 py-3
          text-sm font-semibold
          text-white
          transition
          hover:bg-slate-700
          dark:bg-white
          dark:text-slate-950
          dark:hover:bg-slate-200
        "
      >
        <FaEdit />
        Modifier
      </button>

      <button
        type="button"
        onClick={() =>
          onDuplicate?.(cours)
        }
        className="
          flex items-center justify-center gap-2
          rounded-xl
          border border-slate-200
          px-4 py-3
          text-sm font-semibold
          text-slate-700
          transition
          hover:bg-slate-100
          dark:border-slate-700
          dark:text-slate-200
          dark:hover:bg-slate-800
        "
      >
        <FaCopy />
        Dupliquer
      </button>

      <button
        type="button"
        disabled={deleting}
        onClick={() => onDelete?.(cours)}
        className="
          flex items-center justify-center gap-2
          rounded-xl
          border border-red-200
          bg-red-50
          px-4 py-3
          text-sm font-semibold
          text-red-700
          transition
          hover:bg-red-100
          disabled:cursor-not-allowed
          disabled:opacity-50
          dark:border-red-900/70
          dark:bg-red-950/30
          dark:text-red-300
          dark:hover:bg-red-950/50
        "
      >
        <FaTrash />

        {deleting
          ? "Suppression..."
          : "Supprimer"}
      </button>
    </div>
  );

  return (
    <CoursAgendaDrawerShell
      open={open}
      onClose={onClose}
      title={cours.nom_cours || "Cours"}
      subtitle="Détail du cours"
      footer={footer}
    >
      <div className="space-y-5">
        {/* Accent */}
        <div
          className="h-1.5 w-20 rounded-full"
          style={{
            backgroundColor:
              cours.accentColor ||
              "#64748b",
          }}
        />

        {/* Thème */}
        {cours.theme_cours && (
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
              Thème du cours
            </p>

            <p className="mt-1 text-lg font-semibold text-slate-950 dark:text-white">
              {cours.theme_cours}
            </p>
          </div>
        )}

        {/* Infos principales */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <FaCalendarAlt className="mb-3 text-red-500" />

            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Date
            </p>

            <p className="mt-1 capitalize font-semibold text-slate-900 dark:text-white">
              {formatCourseDate(
                cours.date_cours
              )}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <FaClock className="mb-3 text-red-500" />

            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Horaire
            </p>

            <p className="mt-1 font-semibold text-slate-900 dark:text-white">
              {start} – {end}
            </p>
          </div>
        </div>

        {/* Lieu */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-start gap-3">
            <FaMapMarkerAlt className="mt-1 shrink-0 text-red-500" />

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Lieu
              </p>

              <p className="mt-1 font-medium text-slate-900 dark:text-white">
                {cours.lieu ||
                  "Lieu non renseigné"}
              </p>
            </div>
          </div>
        </div>

        {/* Description */}
        {cours.description_cours && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Description
            </p>

            <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-700 dark:text-slate-300">
              {cours.description_cours}
            </p>
          </div>
        )}

        {/* Personnes */}
        <PeopleSection
          title="Instructeurs"
          icon={FaUser}
          people={cours.enseignants || []}
          emptyText="Aucun instructeur renseigné."
        />

        <PeopleSection
          title="Assistants"
          icon={FaUserFriends}
          people={cours.assistants || []}
          emptyText="Aucun assistant renseigné."
        />
      </div>
    </CoursAgendaDrawerShell>
  );
};

export default CoursAgendaDetailsDrawer;